/**
 * SpawnAllocationPopover
 *
 * Appears when user clicks a spawn-type phase in the ProcessBar.
 * Shows available spawn batches for the batch's strain and lets the user
 * allocate one directly.
 */

import React, { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Check, Loader2, AlertCircle, Package } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/supabase-client'

/**
 * Fetch available spawn batches for a strain (colonised with remaining bags)
 */
async function fetchAvailableSpawn(strainId) {
  const { data, error } = await supabase
    .from('spawn_batches')
    .select('id, code, strain_id, bag_count, bags_remaining, grain_type, inoculation_date, actual_ready_date, status')
    .eq('strain_id', strainId)
    .in('status', ['colonised', 'storage'])
    .gt('bags_remaining', 0)
    .order('actual_ready_date', { ascending: true, nullsFirst: true })

  if (error) throw error
  return data || []
}

/**
 * Link a spawn batch to a production batch
 */
async function linkSpawnToBatch(spawnBatchId, productionBatchId) {
  const { error } = await supabase
    .from('production_batches')
    .update({ required_spawn_batch_id: spawnBatchId })
    .eq('id', productionBatchId)

  if (error) throw error
}

export default function SpawnAllocationPopover({ batch, position, onClose, onAllocated }) {
  const [available, setAvailable] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [allocating, setAllocating] = useState(null) // spawn batch id being allocated

  const strainId = typeof batch.strain === 'object' ? batch.strain?.id : null

  const load = useCallback(async () => {
    if (!strainId) {
      setError('No strain ID available')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await fetchAvailableSpawn(strainId)
      setAvailable(data)
    } catch (err) {
      console.error('[SpawnAllocationPopover] Error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    load()
  }, [load])

  const handleAllocate = async (spawnBatch) => {
    try {
      setAllocating(spawnBatch.id)
      await linkSpawnToBatch(spawnBatch.id, batch.id)
      onAllocated?.(spawnBatch)
      onClose()
    } catch (err) {
      console.error('[SpawnAllocationPopover] Allocation error:', err)
      setError(err.message)
      setAllocating(null)
    }
  }

  const strainName = typeof batch.strain === 'object' ? batch.strain?.name : batch.strain || 'Unknown'

  // Position the popover below the clicked element
  const popoverWidth = 320
  const left = Math.max(8, Math.min(position.x - popoverWidth / 2, window.innerWidth - popoverWidth - 8))
  const top = position.y + 8

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Popover */}
      <div
        className="fixed z-50 bg-white border border-border rounded-lg shadow-lg overflow-hidden"
        style={{ left, top, width: popoverWidth, maxHeight: 360 }}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-border bg-muted/30">
          <div className="text-sm font-semibold">Allocate Spawn</div>
          <div className="text-xs text-muted-foreground">
            Available spawn for {strainName}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
          {loading && (
            <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-destructive">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {!loading && !error && available.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
              No spawn available for this strain.
            </div>
          )}

          {!loading && !error && available.map(spawn => {
            const readyDate = spawn.actual_ready_date
              ? new Date(spawn.actual_ready_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
              : null
            const isAllocating = allocating === spawn.id

            return (
              <button
                key={spawn.id}
                className={cn(
                  'w-full px-3 py-2.5 text-left flex items-center justify-between hover:bg-muted/50 transition-colors border-b border-border/30 last:border-b-0',
                  isAllocating && 'opacity-50 pointer-events-none'
                )}
                onClick={() => handleAllocate(spawn)}
                disabled={!!allocating}
              >
                <div>
                  <div className="text-sm font-medium">{spawn.code}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span>{spawn.bags_remaining} bag{spawn.bags_remaining !== 1 ? 's' : ''} remaining</span>
                    {spawn.grain_type && (
                      <>
                        <span>·</span>
                        <span className="capitalize">{spawn.grain_type}</span>
                      </>
                    )}
                    {readyDate && (
                      <>
                        <span>·</span>
                        <span>Ready {readyDate}</span>
                      </>
                    )}
                  </div>
                </div>

                {isAllocating ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                ) : (
                  <div className="text-xs text-primary font-medium shrink-0">
                    Allocate
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </>,
    document.body
  )
}
